import { ColumnFilterValues, GameDataRefDto } from './game-data.types';

/**
 * Рецепты алхимии — третья таблица игровых данных (после стилей и синтеза).
 * DTO повторяют Swagger (`/admin/game-data/alchemy`) один в один.
 *
 * Как у синтеза: категорий и матрицы применимых полей нет — все поля есть у каждой строки.
 * В отличие от синтеза необязательных полей нет вовсе: создание и изменение требуют всё сразу,
 * очищать нечего, поэтому и `clear` в массовой правке нет.
 *
 * Раньше эти данные жили в Google Sheets, отсюда `POST /import`.
 */

/**
 * Что именно варится — весь список значений из Swagger.
 *
 * Подпись СТРОКИ приходит с бэка (`craftTypeTitle`) на языке запроса — её и показываем в таблице.
 * Подписи для выпадающих списков лежат в `game-data.json` (`alchemy.craftType.*`): списком
 * значений бэк не отдаёт.
 */
export type AlchemyCraftType =
  | 'HUNTING_POTION'
  | 'DEFENSE_POTION'
  | 'SPEED_POTION'
  | 'ELIXIR_POWER'
  | 'ELIXIR_FORTITUDE'
  | 'ELIXIR_LUCK'
  | 'ELIXIR_VIGOR'
  | 'ELIXIR_LIFE'
  | 'ELIXIR_MIGHT'
  | 'ELIXIR_ASPIRATION'
  | 'HERB_LUCK'
  | 'HERB_EXPERIENCE'
  | 'HERB_HUNTING'
  | 'OBLIVION_POTION';

/** Порядок для выпадающих списков — как в Swagger. */
export const ALCHEMY_CRAFT_TYPES: AlchemyCraftType[] = [
  'HUNTING_POTION',
  'DEFENSE_POTION',
  'SPEED_POTION',
  'ELIXIR_POWER',
  'ELIXIR_FORTITUDE',
  'ELIXIR_LUCK',
  'ELIXIR_VIGOR',
  'ELIXIR_LIFE',
  'ELIXIR_MIGHT',
  'ELIXIR_ASPIRATION',
  'HERB_LUCK',
  'HERB_EXPERIENCE',
  'HERB_HUNTING',
  'OBLIVION_POTION',
];

/** Вид зелья. Подпись строки — `potionTypeTitle` с бэка, подписи списков — в локалях. */
export type AlchemyPotionType = 'POTION' | 'ELIXIR' | 'HERB' | 'POTION_CLEAN';

export const ALCHEMY_POTION_TYPES: AlchemyPotionType[] = ['POTION', 'ELIXIR', 'HERB', 'POTION_CLEAN'];

export type AlchemySortOption =
  | 'id'
  | 'id_desc'
  | 'level'
  | 'level_desc'
  | 'craft'
  | 'craft_desc'
  | 'updated'
  | 'updated_desc';

/**
 * Ингредиент рецепта: предмет + количество.
 *
 * `item` приходит только на чтение — на запись уходят ровно `itemId` и `count`
 * (за этим следит `buildAlchemyPayload`).
 */
export interface AlchemyRecipeIngredientDto {
  itemId: number;
  count: number;
  item?: GameDataRefDto | null;
}

/** Рецепт алхимии. */
export interface AlchemyRecipeDto {
  id: number;
  /** Версия строки. Её обязательно вернуть в PUT, иначе правка отклоняется как устаревшая. */
  version: number;
  /** Что получается в результате. */
  productionId: number;
  /** Расшифровка `productionId`. Только на чтение; `name === null` — предмета нет в вики. */
  productionItem?: GameDataRefDto | null;
  level: number;
  craftType: AlchemyCraftType;
  /** Подпись `craftType` на языке запроса — в таблице показываем её, а не свою. */
  craftTypeTitle: string;
  potionType: AlchemyPotionType;
  /** Подпись `potionType` на языке запроса. */
  potionTypeTitle: string;
  limit: number;
  time: number;
  ingredients: AlchemyRecipeIngredientDto[];
  createdAt: string;
  updatedAt: string;
  updatedById?: number | null;
  updatedByUsername?: string | null;
}

/** Тело POST `/` — создание рецепта. Обязательно всё, ингредиентов от 1 до 50. */
export interface CreateAlchemyRecipeDto {
  productionId: number;
  level: number;
  craftType: AlchemyCraftType;
  potionType: AlchemyPotionType;
  limit: number;
  time: number;
  ingredients: AlchemyRecipeIngredientDto[];
}

/** Тело PUT `/{id}` — то же самое плюс `version`. */
export interface UpdateAlchemyRecipeDto extends CreateAlchemyRecipeDto {
  version: number;
}

/** Строка для массовой правки: что меняем и какую версию читали. */
export interface AlchemyRecipeRowVersionDto {
  id: number;
  version: number;
}

/**
 * Тело POST `/bulk`. Применяются ТОЛЬКО переданные поля.
 * `productionId` массово не меняется; `clear` нет — очищать обязательные поля нельзя.
 */
export interface BulkUpdateAlchemyRecipesDto {
  rows: AlchemyRecipeRowVersionDto[];
  level?: number;
  craftType?: AlchemyCraftType;
  potionType?: AlchemyPotionType;
  limit?: number;
  time?: number;
  /** Заменяет список ингредиентов целиком у всех выбранных рецептов. */
  ingredients?: AlchemyRecipeIngredientDto[];
}

export interface BulkUpdateAlchemyRecipesResultDto {
  /** Рецепты, к которым изменения применены. */
  updated: AlchemyRecipeDto[];
  /** Изменённые кем-то другим за это время: НЕ применены, здесь их актуальные значения. */
  conflicted: AlchemyRecipeDto[];
}

export interface BulkDeleteAlchemyRecipesDto {
  ids: number[];
}

/** Результат разового импорта листа алхимии из Google Sheets. */
export interface AlchemyImportResultDto {
  imported: number;
  skipped: number;
  /** Сколько рецептов было в базе до импорта и удалено. */
  replaced: number;
  /** Проблемы в данных листа: битые числа, неизвестные типы, пустые ингредиенты. */
  warnings?: string[];
}

export interface PaginatedAlchemyRecipesDto {
  content: AlchemyRecipeDto[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

/** Параметры GET `/`. */
/**
 * Фильтры по колонкам списка рецептов. `ingredients` повторяется: «57» — предмет в любом
 * количестве, «57:10» — ровно 10, «:10» — любой предмет ровно в 10; рецепт должен содержать каждый. Параметра `itemId` у бэка больше нет.
 */
export const ALCHEMY_FILTER_PARAMS = [
  'id',
  'productionId',
  'ingredients',
  'craftType',
  'potionType',
  'level',
  'limit',
  'time',
] as const;

export interface AlchemyFilters {
  /** Фильтры по колонкам (`ALCHEMY_FILTER_PARAMS`). */
  columns?: ColumnFilterValues;
  sort?: AlchemySortOption;
  page?: number;
  size?: number;
}

/** Ограничения бэка — держим рядом, чтобы валидация формы не расходилась с ним. */
export const ALCHEMY_LIMITS = {
  /** `productionId` и `itemId` ингредиентов. */
  ID_MIN: 1,
  LEVEL_MIN: 1,
  LIMIT_MIN: 0,
  TIME_MIN: 0,
  COUNT_MIN: 1,
  /**
   * Ингредиентов от 1 до 50. В Swagger у создания и изменения стоит `minItems: 0`, но пустой
   * список бэк не принимает (и в `/bulk` `minItems: 1`) — держимся строгого правила.
   */
  INGREDIENTS_MIN: 1,
  INGREDIENTS_MAX: 50,
  /** `rows` в `/bulk` и `ids` в `/bulk/delete`. */
  BULK_MAX_ROWS: 1000,
} as const;
